import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useSelector } from "react-redux";
import wrapperHOC from "@Utils/wrapperHOC";
import { AppState } from "@Store/index";
import { logAppEvent } from "@Utils/appAnalytics";
import { APP_EVENTS, APP_PAGE } from "@Utils/webApputils/analyticEvents";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import {
  ScholarshipFormContext,
  ScholarshipFormModal,
} from "@Pages/Scholarship/ScholarshipFormModal";
import V2Footer from "@Components/V2Footer/V2Footer";
import Navbar from "@Components/Navbar/Navbar";
import Hero from "./components/Hero/Hero";
import CreatorMarquee from "./components/CreatorMarquee/CreatorMarquee";
import Intro from "./components/Intro/Intro";
import Manifesto from "./components/Manifesto/Manifesto";
import Categories from "./components/Categories/Categories";
import Steps from "./components/Steps/Steps";
import Ambassadors from "./components/Ambassadors/Ambassadors";
import Faq from "./components/Faq/Faq";
import Cta from "./components/Cta/Cta";
import styles from "./ScholarshipV2.module.scss";
import useTranslationHook from "hooks/useTranslationHook";
import { ABOUT_URL, BLOG_URL } from "constants/configConstants";
import useIsApp from "hooks/useIsApp";

/**
 * Behaviour carried over from the page this replaces (pages/Scholarship) — the
 * design supplied the layout, not the wiring:
 *
 *   · "Apply Now" opens the Typeform (amber Scholar'26 - Form 1) in
 *     `ScholarshipFormModal`, whose built URL forwards every UTM / click-id parameter
 *     so campaign attribution lands on the form itself.
 *   · the same four analytics calls fire on apply: appEvent CTA_CLICKED, the
 *     `scholarships_cta_click` gtag event, a GTM dataLayer push, and the Meta
 *     Pixel `SubmitApplication` track.
 *   · `version` is tagged "v2" so this page's conversions stay separable from
 *     the "old" / "new" pages already reporting into the same event.
 *
 * The CTAs read `openForm` off ScholarshipFormContext rather than taking a prop,
 * because that is the contract the old page's buttons used and it keeps the
 * button components free of page plumbing.
 */
const ScholarshipV2 = () => {
  const isApp = useIsApp();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const { lastScreen, isUserLoggedIn } = useSelector(({ appData }: AppState) => ({
    lastScreen: appData.lastScreen,
    isUserLoggedIn: appData.isUserLoggedIn,
  }));

  useEffect(() => {
    gtagEventEmitter({
      action: APP_EVENTS.PAGE_VIEWED,
      category: "route",
      screen: APP_PAGE.SCHOLARSHIP,
      page_path: window.location.pathname,
      page_url: window.location.href,
    });
    logAppEvent({
      eventName: APP_EVENTS.PAGE_VIEWED,
      payload: {
        screen: APP_PAGE.SCHOLARSHIP,
        previous_screen: lastScreen,
        is_logged_in: isUserLoggedIn,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openForm = () => {
    logAppEvent({
      eventName: APP_EVENTS.CTA_CLICKED,
      payload: {
        cta_name: "scholarship_apply_now",
        page: APP_PAGE.SCHOLARSHIP,
        is_logged_in: isUserLoggedIn,
        version: "v2",
      },
    });
    gtagEventEmitter({
      action: "scholarships_cta_click",
      category: "scholarship",
    });
    if ((window as any).dataLayer) {
      (window as any).dataLayer.push({ event: "scholarship-apply-clicked" });
    }
    // Meta Pixel — SubmitApplication standard event (Pixel ID: 296478883208201)
    if (typeof (window as any).fbq !== "undefined" && (window as any).fbq !== null) {
      (window as any).fbq("track", "SubmitApplication");
    }
    // Every Apply CTA on this page routes through here — the navbar's, the hero's and the
    // closing card's, the last two via ScholarshipFormContext — so the destination changes
    // in one place. The modal embeds the Typeform with the UTM / click-id parameters
    // forwarded, which is how the scholarship campaign is attributed.
    setIsFormOpen(true);
  };

  const { t } = useTranslationHook();

  return (
    <ScholarshipFormContext.Provider value={{ openForm }}>
      <div className={styles.page}>
        {/* `scholarship.meta.*`, not `scholarship.v2.meta.*` — the keys the page this
            replaces used. The v2 pair ("amberscholar | amber" / 98 chars) reads better
            but contains neither "scholarship" nor the year, which is what /scholarship
            is searched for; swapping the design should not cost the page its title.
            These carry all seven locales and are tagged `generic`, already loaded on
            this route. The `scholarship.v2.meta.*` keys are left in translations.json
            unused, so restoring the shorter pair is a one-line change. */}
        <Helmet title={t("scholarship.meta.title")}>
          <meta name="description" content={t("scholarship.meta.description")} />
          {/* Instrument Sans — the design font, loaded only on this page */}
          <link rel="preconnect" href="https://fonts.googleapis.com" />
          <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
          <link
            rel="stylesheet"
            href="https://fonts.googleapis.com/css2?family=Instrument+Sans:wght@400;500;600;700&display=swap"
          />
        </Helmet>

        <Navbar
          isAppHeader={isApp}
          links={[
            { label: "navbar.v2.blogs", href: BLOG_URL },
            // `redirect` so About us is a real anchor, not a react-router transition.
            // /about is served by the legacy s.web build in production; react-router
            // was matching this app's own /about route first, so clicking here
            // rendered our About page while loading the same URL fresh gave s.web's.
            // Only observable in production — local and staging both serve this app's
            // /about either way.
            { label: "navbar.v2.aboutus", href: ABOUT_URL, redirect: true },
          ]}
          // The bar's CTA opens the application form, so it has to say so. Without an
          // explicit label it fell back to Navbar's default "Visit amber", which
          // promised the homepage and delivered a Typeform. Same key the hero and the
          // closing card use, so all three read "Apply Now".
          ctaLabel="scholarship.v2.applynow"
          ctaOnClick={openForm}
          // The hero already shows this exact pill, so the bar stays collapsed until the
          // hero is behind you. Without this the CTA is always present on desktop —
          // `ctaRevealAfter` is what opts a page into the scroll reveal.
          ctaRevealAfter="[data-sch-hero]"
        />
        <Hero />
        <CreatorMarquee />
        <Intro />
        <Manifesto />
        <Categories />
        <Steps />
        <Ambassadors />
        <Faq />
        <Cta />
        <V2Footer />

        {/* Self-portalling; owns its own body scroll lock. */}
        <ScholarshipFormModal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} />
      </div>
    </ScholarshipFormContext.Provider>
  );
};

export default wrapperHOC(ScholarshipV2, {
  componentName: "ScholarshipV2-Page",
  showForChina: true,
});
